// client/src/socket.js

import { io } from 'socket.io-client';
import { getAccessToken } from './Utilities/Api';

// 🔍 DEBUG: confirm this is the ONLY socket file being loaded
console.log('🧠 [Socket] File Loaded. Singleton initialized.');

let socket = null;

function resolveSocketUrl() {
    const viteSocket =
        typeof import.meta !== 'undefined' ? import.meta.env?.VITE_SOCKET_URL : undefined;
    const viteApi =
        typeof import.meta !== 'undefined' ? import.meta.env?.VITE_API_URL : undefined;

    const craSocket = process.env.REACT_APP_SOCKET_URL;
    const craApi = process.env.REACT_APP_API_BASE_URL;

    const envSocket = viteSocket || craSocket;
    const envApi = viteApi || craApi;
    const isProd = process.env.NODE_ENV === 'production';

    if (envSocket) return String(envSocket).replace(/\/+$/, '');

    if (envApi) {
        try {
            return new URL(envApi).origin.replace(/\/+$/, '');
        } catch {
            // ignore
        }
    }

    if (isProd && typeof window !== 'undefined') {
        return window.location.origin.replace(/\/+$/, '');
    }

    return 'http://localhost:5000';
}

const SOCKET_URL = resolveSocketUrl();
console.log('🔗 [Socket] Resolved URL:', SOCKET_URL);

/**
 * ✅ joinChat function
 * Can still be called manually if needed.
 */
export const joinChat = (userId) => {
    if (socket?.connected && userId) {
        console.log('🆔 [Socket] joinChat() emitting "join" for user:', userId);
        socket.emit('join', userId);
    } else {
        console.warn('⚠️ [Socket] joinChat() skipped. Socket connected?', socket?.connected, 'UserId:', userId);
    }
};

function setSocketAuth() {
    if (!socket) return;
    const token = getAccessToken();
    socket.auth = { token };
    console.log('🔐 [Socket] setSocketAuth() called. Token available:', !!token);
}

export const initSocket = () => {
    if (!socket) {
        console.log('🔌 [Socket] initSocket(): Creating new socket instance...');

        socket = io(SOCKET_URL, {
            withCredentials: true,
            autoConnect: false,
            reconnection: true,
            reconnectionAttempts: 10, // Increased for better stability
            reconnectionDelay: 1000,
            auth: { token: getAccessToken() },
            transports: ['websocket'], // Force websocket to prevent polling delays
        });

        // ✅ THE FIX: Automatic Join Logic
        socket.on('connect', () => {
            console.log('🟢 [Socket] Connected! ID:', socket.id);

            // On every connect (including reconnects), ensure we join the user room
            // Try to find the userId from localStorage or the current session
            const storedUser = localStorage.getItem('user'); // Note: Make sure 'user' key actually exists in LS
            const storedUserId = localStorage.getItem('userId'); // Check your specific key usage

            // Prefer simple ID if available, otherwise parse object
            let finalUserId = storedUserId;

            if (!finalUserId && storedUser) {
                try {
                    const parsed = JSON.parse(storedUser);
                    finalUserId = parsed._id || parsed.id;
                } catch (e) {
                    console.error('❌ [Socket] Failed to parse user from localStorage');
                }
            }

            if (finalUserId) {
                console.log('🆔 [Socket] Auto-joining room for user:', finalUserId);
                socket.emit('join', finalUserId);
            } else {
                console.warn('⚠️ [Socket] Connected but no User ID found to join room.');
            }
        });

        socket.on('disconnect', (reason) => {
            console.warn('🔌 [Socket] Disconnected:', reason);
        });

        socket.on('connect_error', (err) => {
            console.error('❌ [Socket] Connection Error:', err?.message || err);
        });
    } else {
        console.log("🔌 [Socket] initSocket(): Socket already exists. Reusing.");
    }

    setSocketAuth();
    return socket;
};

export const getSocket = () => {
    if (!socket) {
        console.warn("⚠️ [Socket] getSocket() called but socket is null.");
    }
    return socket;
};

export const connectSocket = () => {
    if (!socket) {
        console.warn("⚠️ [Socket] connectSocket() called but socket is null. Initializing...");
        initSocket();
    }
    if (socket.connected || socket.connecting) {
        console.log("🔌 [Socket] Already connected/connecting.");
        return;
    }
    console.log("🔌 [Socket] calling .connect()");
    setSocketAuth();
    socket.connect();
};

export const disconnectSocket = () => {
    if (socket) {
        console.log("🔌 [Socket] calling .disconnect()");
        socket.disconnect();
    }
};

export const reconnectWithFreshToken = () => {
    if (!socket) return;
    console.log("🔄 [Socket] Reconnecting with fresh token...");
    setSocketAuth();
    if (socket.connected) {
        socket.disconnect();
    }
    socket.connect();
};

export const emitHeartbeat = (userId) => {
    if (socket?.connected && userId) {
        // console.log("💓 [Socket] Sending Heartbeat..."); // Uncomment to debug heartbeats
        socket.emit('heartbeat', userId);
    }
};
/**
 * ✅ NEW: joinChatRoom function
 * Tells the server "I am now looking at this specific conversation"
 */
export const joinChatRoom = (chatId) => {
    if (socket?.connected && chatId) {
        console.log('📡 [Socket] joinChatRoom() emitting "join_chat" for:', chatId);
        socket.emit('join_chat', chatId);
    }
};

/**
 * ✅ NEW: leaveChatRoom function
 * Stops listening to a specific conversation (good for performance)
 */
export const leaveChatRoom = (chatId) => {
    if (socket?.connected && chatId) {
        console.log('📡 [Socket] leaveChatRoom() emitting "leave_chat" for:', chatId);
        socket.emit('leave_chat', chatId);
    }
};